import { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import { useManualConfirmedPairs } from '../api/useEntry';
import {
  useOpenerGroupStart,
  useOpenerGroupStop,
  useOpenerGroupsPodState
} from '../api/useOpenerGroupPods';
import {
  GROUP_POD_STATUS_DOT_COLORS,
  GROUP_POD_STATUS_LABELS,
  canStartGroup,
  deriveGroupPodStatus
} from '../components/entry/groupPodStatus';
import { FinishGroupButton } from '../components/entry/FinishGroupButton';
import { FinishResultsView } from '../components/bucket/FinishResultsView';
import {
  OpenerGroup,
  useAssignPairGroup,
  useBulkAssignPairGroup,
  useDeleteGroup,
  useHiddenManualConfirmedPairs,
  useOpenerGroups
} from '../api/useGroups';
import { CreateGroupDialog } from '../components/bucket/CreateGroupDialog';
import {
  BUCKET_ALL_GROUPS,
  BucketView,
  UNGROUPED,
  expiredAgoLabel,
  filterByGroup,
  filterBySearch,
  splitByExpiry
} from '../components/bucket/bucketFilters';
import { sortGroups } from '../utils/sortGroups';
import { useShowInOpener } from '../components/bucket/useShowInOpener';
import {
  CATEGORY_LABELS,
  PredictionCategory
} from '../constants/categories';
import { ManualConfirmedPair } from '../types/entry.types';

/* ------------------------------------------------------------------ */
/* Small render helpers                                                */
/* ------------------------------------------------------------------ */

const categoryLabel = (category?: string | null): string =>
  category
    ? (CATEGORY_LABELS[category as PredictionCategory] ?? category)
    : '—';

const formatExpiry = (expiryDate?: string | null): string => {
  if (!expiryDate) return '—';
  const t = new Date(expiryDate);
  if (Number.isNaN(t.getTime())) return '—';
  return t.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
};

const GroupColorDot = ({ color }: { color?: string }) =>
  color ? (
    <Box
      component="span"
      sx={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: color,
        flexShrink: 0,
        display: 'inline-block'
      }}
    />
  ) : null;

/** Read-only group chip — used on the Expired / Hidden views. */
const GroupChip = ({ group }: { group: OpenerGroup | undefined }) =>
  group ? (
    <Chip
      size="small"
      icon={
        group.color ? (
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: `${group.color} !important`,
              ml: '6px !important'
            }}
          />
        ) : undefined
      }
      label={group.name}
      variant="outlined"
      sx={{ fontSize: '0.7rem', height: 20 }}
    />
  ) : (
    <Typography sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
      Ungrouped
    </Typography>
  );

const ExternalTitleLink = ({
  prefix,
  title,
  url
}: {
  prefix: string;
  title?: string;
  url?: string;
}) => {
  if (!title) return null;
  return (
    <Typography
      sx={{
        fontSize: '0.7rem',
        color: 'grey.500',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        minWidth: 0
      }}
      noWrap
    >
      <Box component="span" sx={{ color: 'grey.600', flexShrink: 0 }}>
        {prefix}
      </Box>
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {title}
      </Box>
      {url && (
        <Link
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'inline-flex', color: 'grey.500', flexShrink: 0 }}
        >
          <OpenInNewIcon sx={{ fontSize: '0.8rem' }} />
        </Link>
      )}
    </Typography>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export const BucketPage = () => {
  const { data: activeData, isLoading } = useManualConfirmedPairs();
  const { data: groups } = useOpenerGroups();

  const [view, setView] = useState<BucketView>('active');
  const { data: hiddenData, isLoading: hiddenLoading } =
    useHiddenManualConfirmedPairs(view === 'hidden');

  const assignMutation = useAssignPairGroup();
  const bulkAssignMutation = useBulkAssignPairGroup();
  const deleteMutation = useDeleteGroup();
  const showInOpenerMutation = useShowInOpener();

  // Per-group pod state for the rail's status dot + compact start/stop.
  const { data: podStates } = useOpenerGroupsPodState();
  const startGroupMutation = useOpenerGroupStart();
  const stopGroupMutation = useOpenerGroupStop();
  const podStateByGroup = useMemo(
    () => new Map((podStates ?? []).map(s => [s.groupId, s])),
    [podStates]
  );

  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState<string>(BUCKET_ALL_GROUPS);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // One dialog covers create (group: null) and rename/edit (group set).
  const [groupDialog, setGroupDialog] = useState<{
    open: boolean;
    group: OpenerGroup | null;
  }>({ open: false, group: null });
  const [deleteTarget, setDeleteTarget] = useState<OpenerGroup | null>(null);

  // Selection is view/filter-scoped — switching tab or group drops it so a
  // bulk action never silently touches rows that are no longer on screen.
  useEffect(() => {
    setSelected(new Set());
  }, [view, groupFilter]);

  const activePairs: ManualConfirmedPair[] = useMemo(
    () => activeData ?? [],
    [activeData]
  );
  const hiddenPairs: ManualConfirmedPair[] = useMemo(
    () => hiddenData ?? [],
    [hiddenData]
  );

  const sortedGroups = useMemo(() => sortGroups(groups), [groups]);
  const groupsById = useMemo(
    () => new Map((groups ?? []).map(g => [g._id, g])),
    [groups]
  );

  // Ticking clock (30s) so the Active/Expired split AND the row's
  // "expired N ago" badge share one fresh `now` — a pair crossing its
  // expiry while the page is open migrates tabs on the next tick instead
  // of staying frozen at mount time.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // Search + group filters apply to every view; the Active/Expired split
  // only applies to the non-hidden universe (a hidden pair is hidden no
  // matter its expiry).
  const { active, expired } = useMemo(() => {
    const filtered = filterByGroup(
      filterBySearch(activePairs, search),
      groupFilter
    );
    return splitByExpiry(filtered, now);
  }, [activePairs, search, groupFilter, now]);

  const hiddenVisible = useMemo(
    () => filterByGroup(filterBySearch(hiddenPairs, search), groupFilter),
    [hiddenPairs, search, groupFilter]
  );

  const visiblePairs =
    view === 'active' ? active : view === 'expired' ? expired : view === 'hidden' ? hiddenVisible : [];

  // Virtualize the table body: the manual-confirmed universe is ~2-3k pairs and
  // every row used to mount a <Select> + checkbox + Typography nodes at once,
  // freezing the page on open. The virtualizer mounts only the rows in (or near)
  // the viewport; search / group-filter / select-all still operate over the full
  // `visiblePairs` set because that array is unchanged — only its rendering is windowed.
  const scrollParentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: visiblePairs.length,
    getScrollElement: () => scrollParentRef.current,
    estimateSize: () => 48,
    overscan: 12
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
      : 0;

  const ungroupedCount = useMemo(
    () => activePairs.filter(p => p.openerGroupId == null).length,
    [activePairs]
  );

  // Per-group rail counts, client-computed from the SAME loaded active
  // (non-hidden) pair list the "All"/"Ungrouped" counts use. Mixing the
  // server's `pairCount` (separate query, own refresh cadence) with the
  // client-computed All/Ungrouped numbers let the rail disagree with
  // itself; one source keeps it internally consistent.
  const groupCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of activePairs) {
      if (p.openerGroupId == null) continue;
      counts.set(p.openerGroupId, (counts.get(p.openerGroupId) ?? 0) + 1);
    }
    return counts;
  }, [activePairs]);

  // Bulk actions operate on the intersection of the selection and the rows
  // currently on screen — never on rows a filter has hidden since the click.
  const selectedVisible = useMemo(
    () => visiblePairs.filter(p => selected.has(p._id)),
    [visiblePairs, selected]
  );

  const selectable = view !== 'expired' && view !== 'results';
  const allVisibleSelected =
    visiblePairs.length > 0 && selectedVisible.length === visiblePairs.length;

  const toggleRow = (pairId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(pairId)) next.delete(pairId);
      else next.add(pairId);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected(
      allVisibleSelected ? new Set() : new Set(visiblePairs.map(p => p._id))
    );
  };

  const handleAssign = (pairId: string, value: string) => {
    assignMutation.mutate({
      pairId,
      groupId: value === UNGROUPED ? null : value
    });
  };

  // The async handlers below are invoked as `void handler().catch(() => {})`
  // at their call sites: a failed mutateAsync is already surfaced by the
  // global MutationCache.onError toast, and without the catch it would also
  // escape as an unhandled promise rejection.
  const handleBulkAssign = async (value: string) => {
    if (selectedVisible.length === 0) return;
    await bulkAssignMutation.mutateAsync({
      pairIds: selectedVisible.map(p => p._id),
      groupId: value === UNGROUPED ? null : value
    });
    setSelected(new Set());
  };

  const handleBulkShow = async () => {
    if (selectedVisible.length === 0) return;
    await showInOpenerMutation.mutateAsync(selectedVisible.map(p => p._id));
    setSelected(new Set());
  };

  const handleDeleteGroup = async () => {
    if (!deleteTarget) return;
    await deleteMutation.mutateAsync(deleteTarget._id);
    if (groupFilter === deleteTarget._id) setGroupFilter(BUCKET_ALL_GROUPS);
    setDeleteTarget(null);
  };

  const loading = view === 'hidden' ? hiddenLoading : isLoading;

  /* ---------------------------------------------------------------- */

  const railItem = (
    key: string,
    label: string,
    count: number | null,
    group?: OpenerGroup
  ) => {
    const isSelected = groupFilter === key;
    const groupPodState = group ? podStateByGroup.get(group._id) : undefined;
    const podStatus = group ? deriveGroupPodStatus(groupPodState) : null;
    const observedTier = groupPodState?.observedTier;
    const tierChip = observedTier ?? groupPodState?.recommendedTier;
    return (
      <Stack
        key={key}
        direction="row"
        alignItems="center"
        spacing={0.75}
        onClick={() => setGroupFilter(key)}
        sx={{
          px: 1,
          py: 0.5,
          borderRadius: 1,
          cursor: 'pointer',
          backgroundColor: isSelected ? 'action.selected' : 'transparent',
          '&:hover': { backgroundColor: 'action.hover' },
          '&:hover .rail-actions': { opacity: 1, maxWidth: 200 }
        }}
      >
        <GroupColorDot color={group?.color} />
        <Typography
          sx={{
            fontSize: '0.8rem',
            color: isSelected ? 'common.white' : 'grey.400',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {label}
        </Typography>
        {group?.testAccount && (
          <Tooltip title="This group trades against a separate low-balance TEST account.">
            <Chip
              label="TEST ACCOUNT"
              size="small"
              color="warning"
              sx={{ fontSize: '0.55rem', height: 16, fontWeight: 700 }}
            />
          </Tooltip>
        )}
        {group?.dynamic && (
          <Tooltip title="Dynamic group — future pairs matching its category rule are auto-assigned.">
            <Chip
              label="auto"
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.6rem', height: 16 }}
            />
          </Tooltip>
        )}
        {count !== null && (
          <Chip
            label={count}
            size="small"
            sx={{ fontSize: '0.65rem', height: 18 }}
          />
        )}
        {tierChip && (
          <Tooltip
            title={
              observedTier
                ? `Running engine tier: ${observedTier.toUpperCase()}`
                : `Recommended engine tier: ${tierChip.toUpperCase()} (auto-sizer estimate — no pod running)`
            }
          >
            <Chip
              label={
                observedTier
                  ? tierChip.toUpperCase()
                  : `~${tierChip.toUpperCase()}`
              }
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.6rem', height: 16 }}
            />
          </Tooltip>
        )}
        {podStatus && (
          <Tooltip title={`Pods: ${GROUP_POD_STATUS_LABELS[podStatus]}`}>
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: GROUP_POD_STATUS_DOT_COLORS[podStatus],
                flexShrink: 0,
                display: 'inline-block'
              }}
            />
          </Tooltip>
        )}
        {group && (
          <Stack
            direction="row"
            className="rail-actions"
            sx={{
              opacity: 0,
              maxWidth: 0,
              overflow: 'hidden',
              transition: 'opacity 0.15s, max-width 0.15s'
            }}
          >
            {podStatus &&
              (canStartGroup(podStatus, false) ? (
                <Tooltip title="Start group pods (engine + feed)">
                  <span>
                    <IconButton
                      size="small"
                      disabled={startGroupMutation.isLoading}
                      onClick={e => {
                        e.stopPropagation();
                        startGroupMutation.mutate({
                          groupId: group._id,
                          testAccount: false
                        });
                      }}
                    >
                      <PlayArrowIcon sx={{ fontSize: '0.85rem' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="Stop group pods — pauses the group's rules and drains its pods">
                  <span>
                    <IconButton
                      size="small"
                      disabled={stopGroupMutation.isLoading}
                      onClick={e => {
                        e.stopPropagation();
                        stopGroupMutation.mutate(group._id);
                      }}
                    >
                      <PowerSettingsNewIcon sx={{ fontSize: '0.85rem' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              ))}
            <FinishGroupButton groupId={group._id} groupName={group.name} />
            <Tooltip title="Rename / edit">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  setGroupDialog({ open: true, group });
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: '0.85rem' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete group">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  setDeleteTarget(group);
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: '0.85rem' }} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        px: 2,
        pt: 1,
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Title bar */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1, flexShrink: 0 }}
      >
        <Typography variant="h5" sx={{ color: 'common.white' }}>
          Pairs Bucket
        </Typography>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Search by title…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{
            width: 240,
            '& .MuiOutlinedInput-root': { fontSize: '0.75rem', height: 28 },
            '& .MuiOutlinedInput-input': { py: 0.5, px: 1 }
          }}
          inputProps={{ 'aria-label': 'Search pairs by title' }}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setGroupDialog({ open: true, group: null })}
          sx={{ fontSize: '0.7rem' }}
        >
          New group
        </Button>
      </Stack>

      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
        {/* Group rail */}
        <Box
          sx={{
            width: 230,
            flexShrink: 0,
            overflowY: 'auto',
            borderRight: theme => `1px solid ${theme.palette.grey[900]}`,
            pr: 1
          }}
        >
          <Stack spacing={0.25}>
            {railItem(BUCKET_ALL_GROUPS, 'All', activePairs.length)}
            {railItem(UNGROUPED, 'Ungrouped', ungroupedCount)}
            {sortedGroups.map(g =>
              railItem(g._id, g.name, groupCounts.get(g._id) ?? 0, g)
            )}
          </Stack>
        </Box>

        {/* Main column: view tabs + bulk bar + table */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flexShrink: 0 }}
          >
            <Tabs
              value={view}
              onChange={(_, v: BucketView) => setView(v)}
              sx={{
                minHeight: 32,
                '& .MuiTab-root': {
                  minHeight: 32,
                  py: 0.5,
                  fontSize: '0.75rem'
                }
              }}
            >
              <Tab value="active" label={`Active (${active.length})`} />
              <Tab value="expired" label={`Expired (${expired.length})`} />
              <Tab
                value="hidden"
                label={
                  view === 'hidden' && !hiddenLoading
                    ? `Hidden (${hiddenVisible.length})`
                    : 'Hidden'
                }
              />
              <Tab value="results" label="Results" />
            </Tabs>
            {search.trim() && (
              <Typography sx={{ fontSize: '0.7rem', color: 'grey.500' }}>
                {visiblePairs.length} matched
              </Typography>
            )}
          </Stack>

          {view === 'expired' && (
            <Typography
              sx={{ fontSize: '0.7rem', color: 'grey.500', mb: 0.5 }}
            >
              Resolved events, longest past expiry first — informational only;
              the daily cleanup retires them.
            </Typography>
          )}

          {/* Bulk bar */}
          {selectable && selectedVisible.length > 0 && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                py: 0.5,
                px: 1,
                mb: 0.5,
                borderRadius: 1,
                backgroundColor: 'action.selected',
                flexShrink: 0
              }}
            >
              <Typography sx={{ fontSize: '0.75rem' }}>
                {selectedVisible.length} selected
              </Typography>
              {view === 'hidden' ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityOutlinedIcon />}
                  onClick={() => void handleBulkShow().catch(() => {})}
                  disabled={showInOpenerMutation.isLoading}
                  sx={{ fontSize: '0.7rem' }}
                >
                  {showInOpenerMutation.isLoading ? (
                    <CircularProgress size={14} />
                  ) : (
                    'Show in opener'
                  )}
                </Button>
              ) : (
                <Select
                  size="small"
                  value=""
                  displayEmpty
                  disabled={bulkAssignMutation.isLoading}
                  onChange={e =>
                    void handleBulkAssign(e.target.value).catch(() => {})
                  }
                  sx={{ fontSize: '0.75rem', minWidth: 170, height: 28 }}
                  inputProps={{ 'aria-label': 'Assign selected to group' }}
                  renderValue={() =>
                    bulkAssignMutation.isLoading
                      ? 'Assigning…'
                      : 'Assign to group…'
                  }
                >
                  <MenuItem value={UNGROUPED} sx={{ fontSize: '0.75rem' }}>
                    Ungrouped
                  </MenuItem>
                  {sortedGroups.map(g => (
                    <MenuItem
                      key={g._id}
                      value={g._id}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <GroupColorDot color={g.color} />
                        <span>{g.name}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              )}
              <Box sx={{ flex: 1 }} />
              <Button
                size="small"
                onClick={() => setSelected(new Set())}
                sx={{ fontSize: '0.7rem' }}
              >
                Clear
              </Button>
            </Stack>
          )}

          {view === 'results' && <FinishResultsView />}

          {/* Table */}
          {view !== 'results' && (loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : visiblePairs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography sx={{ color: 'grey.500', fontSize: '0.85rem' }}>
                {view === 'hidden'
                  ? 'No hidden pairs.'
                  : view === 'expired'
                    ? 'No expired pairs.'
                    : 'No pairs match the current filters.'}
              </Typography>
            </Box>
          ) : (
            <TableContainer
              ref={scrollParentRef}
              sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {selectable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={allVisibleSelected}
                          indeterminate={
                            selectedVisible.length > 0 && !allVisibleSelected
                          }
                          onChange={toggleAllVisible}
                          inputProps={{ 'aria-label': 'Select all pairs' }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ fontSize: '0.7rem' }}>Market</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', width: '1%', whiteSpace: 'nowrap', px: 1 }}>Category</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', width: '1%', whiteSpace: 'nowrap', px: 1 }}>Group</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', width: '1%', whiteSpace: 'nowrap', px: 1 }}>Expiry</TableCell>
                    {view === 'hidden' && (
                      <TableCell sx={{ fontSize: '0.7rem', width: '1%', px: 1 }} align="right">
                        Actions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paddingTop > 0 && (
                    <TableRow style={{ height: paddingTop }}>
                      <TableCell
                        colSpan={100}
                        sx={{ p: 0, border: 'none' }}
                        aria-hidden
                      />
                    </TableRow>
                  )}
                  {virtualRows.map(virtualRow => {
                    const pair = visiblePairs[virtualRow.index];
                    const agoLabel = expiredAgoLabel(pair, now);
                    const group = pair.openerGroupId
                      ? groupsById.get(pair.openerGroupId)
                      : undefined;
                    return (
                      <TableRow
                        key={pair._id}
                        hover
                        data-index={virtualRow.index}
                        ref={rowVirtualizer.measureElement}
                      >
                        {selectable && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={selected.has(pair._id)}
                              onChange={() => toggleRow(pair._id)}
                              inputProps={{
                                'aria-label': `Select ${pair.pairName}`
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell sx={{ maxWidth: 420 }}>
                          <Typography sx={{ fontSize: '0.8rem' }} noWrap>
                            {pair.pairName}
                          </Typography>
                          <ExternalTitleLink
                            prefix="K"
                            title={pair.kalshiTitle}
                            url={pair.kalshi?.marketUrl}
                          />
                          <ExternalTitleLink
                            prefix="P"
                            title={pair.polymarketTitle}
                            url={pair.polymarket?.marketUrl}
                          />
                        </TableCell>
                        <TableCell sx={{ px: 1, whiteSpace: 'nowrap' }}>
                          <Typography
                            sx={{ fontSize: '0.75rem', color: 'grey.400' }}
                          >
                            {categoryLabel(pair.category)}
                            {pair.subcategory ? ` / ${pair.subcategory}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ px: 1, whiteSpace: 'nowrap' }}>
                          {view === 'active' ? (
                            <Select
                              size="small"
                              value={pair.openerGroupId ?? UNGROUPED}
                              onChange={e =>
                                handleAssign(pair._id, e.target.value)
                              }
                              sx={{
                                fontSize: '0.75rem',
                                minWidth: 120,
                                height: 26
                              }}
                              inputProps={{
                                'aria-label': `Group for ${pair.pairName}`
                              }}
                            >
                              <MenuItem
                                value={UNGROUPED}
                                sx={{ fontSize: '0.75rem' }}
                              >
                                Ungrouped
                              </MenuItem>
                              {sortedGroups.map(g => (
                                <MenuItem
                                  key={g._id}
                                  value={g._id}
                                  sx={{ fontSize: '0.75rem' }}
                                >
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.75}
                                  >
                                    <GroupColorDot color={g.color} />
                                    <span>{g.name}</span>
                                  </Stack>
                                </MenuItem>
                              ))}
                            </Select>
                          ) : (
                            <GroupChip group={group} />
                          )}
                        </TableCell>
                        <TableCell sx={{ px: 1, whiteSpace: 'nowrap' }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                          >
                            <Typography
                              sx={{ fontSize: '0.75rem', color: 'grey.400' }}
                            >
                              {formatExpiry(pair.expiryDate)}
                            </Typography>
                            {agoLabel && (
                              <Chip
                                label={agoLabel}
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 18 }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        {view === 'hidden' && (
                          <TableCell align="right" sx={{ px: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<VisibilityOutlinedIcon />}
                              disabled={showInOpenerMutation.isLoading}
                              onClick={() =>
                                showInOpenerMutation.mutate([pair._id])
                              }
                              sx={{ fontSize: '0.65rem' }}
                            >
                              Show in opener
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                  {paddingBottom > 0 && (
                    <TableRow style={{ height: paddingBottom }}>
                      <TableCell
                        colSpan={100}
                        sx={{ p: 0, border: 'none' }}
                        aria-hidden
                      />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ))}
        </Box>
      </Box>

      {/* Create / edit group dialog */}
      <CreateGroupDialog
        open={groupDialog.open}
        group={groupDialog.group}
        onClose={() => setGroupDialog({ open: false, group: null })}
      />

      {/* Delete-group confirm */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem' }}>
          Delete group — {deleteTarget?.name}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem' }}>
            Delete &quot;{deleteTarget?.name}&quot;? Its{' '}
            {deleteTarget?.pairCount ?? 0} pair
            {(deleteTarget?.pairCount ?? 0) === 1 ? '' : 's'} return to
            Ungrouped.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={() => void handleDeleteGroup().catch(() => {})}
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? (
              <CircularProgress size={16} />
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
